import React, { useState, useRef, useEffect } from "react";
import { ToggleLeft, ToggleRight, CalendarDays, ChevronDown } from "lucide-react";

/* ── constants ─────────────────────────────────────────────── */
const PRESETS = [
  { label: "Today",        value: "today"        },
  { label: "Yesterday",    value: "yesterday"    },
  { label: "Last 7 Days",  value: "last_7_days"  },
  { label: "Last 30 Days", value: "last_30_days" },
  { label: "This Month",   value: "this_month"   },
  { label: "Last Month",   value: "last_month"   },
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS   = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTHS_S = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const PRESET_NAMES = {
  today: "Today", yesterday: "Yesterday", last_7_days: "Last 7 Days",
  last_30_days: "Last 30 Days", this_month: "This Month", last_month: "Last Month",
  last_week: "Last Week", last_year: "Last Year",
};

/* ── helpers ───────────────────────────────────────────────── */
const pad  = (n) => String(n).padStart(2, "0");
const dStr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

const todayStr = () => {
  const t = new Date();
  return dStr(t.getFullYear(), t.getMonth(), t.getDate());
};

const computeRange = (preset) => {
  const now = new Date();
  const t   = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const s   = (d) => dStr(d.getFullYear(), d.getMonth(), d.getDate());
  switch (preset) {
    case "today":      return { start: s(t), end: s(t) };
    case "yesterday": {
      const y = new Date(t); y.setDate(t.getDate() - 1);
      return { start: s(y), end: s(y) };
    }
    case "last_7_days": {
      const a = new Date(t); a.setDate(t.getDate() - 6);
      return { start: s(a), end: s(t) };
    }
    case "last_30_days": {
      const a = new Date(t); a.setDate(t.getDate() - 29);
      return { start: s(a), end: s(t) };
    }
    case "this_month":
      return {
        start: s(new Date(now.getFullYear(), now.getMonth(), 1)),
        end:   s(t),
      };
    case "last_week": {
      const a = new Date(t); a.setDate(t.getDate() - 7);
      const b = new Date(t); b.setDate(t.getDate() - 1);
      return { start: s(a), end: s(b) };
    }
    case "last_month":
      return {
        start: s(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        end:   s(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    case "last_year": {
      const y = now.getFullYear() - 1;
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    }
    default: return { start: s(t), end: s(t) };
  }
};

const fmtLabel = (filter) => {
  if (!filter) return "Select Date";
  const fmt = (str) => {
    const [, m, d] = str.split("-");
    return `${MONTHS_S[parseInt(m) - 1]} ${parseInt(d)}`;
  };
  if (filter.type === "preset") {
    const r    = computeRange(filter.preset);
    const name = PRESET_NAMES[filter.preset] || filter.preset;
    if (filter.preset === "last_year") return `Last Year · ${r.start.slice(0, 4)}`;
    if (r.start === r.end) return `${name} · ${fmt(r.start)}`;
    return `${name} · ${fmt(r.start)} – ${fmt(r.end)}`;
  }
  if (filter.type === "single") {
    const [y, m, d] = filter.date.split("-");
    return `${MONTHS_S[parseInt(m) - 1]} ${parseInt(d)}, ${y}`;
  }
  if (filter.type === "range") {
    return `${fmt(filter.start)} – ${fmt(filter.end)}, ${filter.end.slice(0, 4)}`;
  }
  return "Select Date";
};

/* ── component ─────────────────────────────────────────────── */
const SalesDatePicker = ({ onChange, defaultPreset = "last_month" }) => {
  const now = new Date();

  const initRange      = computeRange(defaultPreset);
  const [iy, im]       = initRange.start.split("-").map(Number);

  /* committed (applied) filter */
  const [filter, setFilter] = useState({ type: "preset", preset: defaultPreset });

  /* panel visibility */
  const [open, setOpen] = useState(false);

  /* pending panel state */
  const [panelPreset,  setPanelPreset]  = useState(defaultPreset);
  const [panelStart,   setPanelStart]   = useState(initRange.start);
  const [panelEnd,     setPanelEnd]     = useState(initRange.end);
  const [isRange,      setIsRange]      = useState(false);
  const [picking,      setPicking]      = useState(null);
  const [hover,        setHover]        = useState(null);
  const [viewMonth,    setViewMonth]    = useState(im - 1);
  const [viewYear,     setViewYear]     = useState(iy);

  const wrapRef = useRef(null);
  const today   = todayStr();
  const years   = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 4 + i);

  /* close on outside click */
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const handleOpen = () => {
    if (open) { setOpen(false); return; }
    /* sync panel to committed filter */
    const range =
      filter.type === "preset"  ? computeRange(filter.preset) :
      filter.type === "single"  ? { start: filter.date, end: filter.date } :
                                  { start: filter.start, end: filter.end };
    setPanelPreset(filter.type === "preset" ? filter.preset : null);
    setPanelStart(range.start);
    setPanelEnd(range.end);
    setPicking(null);
    setIsRange(false);
    const [y, m] = range.start.split("-").map(Number);
    setViewYear(y);
    setViewMonth(m - 1);
    setOpen(true);
  };

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const handlePreset = (value) => {
    const { start, end } = computeRange(value);
    setPanelPreset(value);
    setPanelStart(start);
    setPanelEnd(end);
    setPicking(null);
    const [y, m] = start.split("-").map(Number);
    setViewYear(y);
    setViewMonth(m - 1);
  };

  const handleDayClick = (day) => {
    const ds = dStr(viewYear, viewMonth, day);
    setPanelPreset(null);
    if (!isRange) {
      setPanelStart(ds);
      setPanelEnd(ds);
      return;
    }
    if (!picking) {
      setPicking(ds);
      setPanelStart(ds);
      setPanelEnd(null);
    } else {
      const [s, e] = picking <= ds ? [picking, ds] : [ds, picking];
      setPicking(null);
      setPanelStart(s);
      setPanelEnd(e);
    }
  };

  const handleApply = () => {
    let f;
    if (panelPreset) {
      f = { type: "preset", preset: panelPreset };
    } else if (!panelEnd || panelStart === panelEnd) {
      f = { type: "single", date: panelStart };
    } else {
      f = { type: "range", start: panelStart, end: panelEnd };
    }
    setFilter(f);
    onChange(f);
    setOpen(false);
  };

  /* day cell classification */
  const dayStr     = (day) => dStr(viewYear, viewMonth, day);
  const hasRange   = panelStart && panelEnd && panelStart !== panelEnd;
  const getEffEnd  = () => isRange && picking && hover ? hover : (isRange && picking ? null : panelEnd);

  const cellCls = (day) => {
    const d   = dayStr(day);
    const eff = getEffEnd();

    const inRange = (() => {
      if (!panelStart) return false;
      const end = isRange && picking ? eff : (hasRange ? panelEnd : null);
      if (!end) return false;
      const [s, e] = panelStart <= end ? [panelStart, end] : [end, panelStart];
      return d > s && d < e;
    })();

    const isStart = (() => {
      if (isRange && picking) return d === picking;
      return hasRange && d === panelStart;
    })();

    const isEnd = (() => {
      if (isRange && picking) return eff && d === eff;
      return hasRange && d === panelEnd;
    })();

    let cls = "sdp-cell";
    if (inRange) cls += " sdp-in-range";
    if (isStart) cls += " sdp-range-s";
    if (isEnd)   cls += " sdp-range-e";
    return cls;
  };

  const dayCls = (day) => {
    const d        = dayStr(day);
    const isT      = d === today;
    const isSingle = panelStart === panelEnd && d === panelStart && !isRange;
    const isStart  = hasRange && d === panelStart && !(isRange && picking);
    const isEnd_   = hasRange && d === panelEnd   && !(isRange && picking);
    const isPend   = isRange && d === picking;
    const isHovEnd = isRange && picking && hover === d;

    let cls = "day text-dark";
    if (isT) cls += " sdp-today-day";
    if (isSingle || isStart || isEnd_ || isPend || isHovEnd) cls += " selected";
    return cls;
  };

  return (
    <div className="sdp-wrap" ref={wrapRef}>

      {/* ── Trigger ─────────────────────────── */}
      <button className={`sdp-trigger${open ? " open" : ""}`} onClick={handleOpen}>
        <CalendarDays size={14} className="sdp-trigger-icon" />
        <span className="sdp-trigger-label">{fmtLabel(filter)}</span>
        <ChevronDown size={13} className={`sdp-trigger-chev${open ? " open" : ""}`} />
      </button>

      {/* ── Panel ── */}
      {open && (
        <div className="sdp-panel">
          <div className="sdp-body">

            {/* Left sidebar — preset list */}
            <div className="sdp-sidebar">
              {PRESETS.map((p) => (
                <button
                  key={p.value}
                  className={`sdp-sitem${panelPreset === p.value ? " active" : ""}`}
                  onClick={() => handlePreset(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Right calendar */}
            <div className="sdp-cal">

              {/* Calendar header */}
              <div className="calendar-header mb-0">
                <div className="calender-btn" onClick={prevMonth}>
                  <i className="fa-solid fa-angle-left" style={{ color: "#607D8B" }} />
                </div>
                <div className="calendar-selectors">
                  <select
                    value={viewMonth}
                    onChange={(e) => setViewMonth(Number(e.target.value))}
                    className="py-1 form-select w-100"
                  >
                    {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                  </select>
                  <select
                    value={viewYear}
                    onChange={(e) => setViewYear(Number(e.target.value))}
                    className="py-1 form-select w-100"
                  >
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="calender-btn" onClick={nextMonth}>
                  <i className="fa-solid fa-chevron-right" style={{ color: "#607D8B" }} />
                </div>
              </div>

              {/* Range toggle */}
              <div className="sdp-range-row mt-2">
                <button
                  className={`sdp-range-toggle${isRange ? " on" : ""}`}
                  onClick={() => { setIsRange((r) => !r); setPicking(null); }}
                >
                  {isRange ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                  <span>{isRange ? "Range on" : "Range"}</span>
                </button>
                {isRange && picking && <span className="sdp-hint">Pick end date…</span>}
              </div>

              {/* Weekdays */}
              <div className="calendar-weekdays mt-2">
                {WEEKDAYS.map((d) => <div key={d} className="weekday">{d}</div>)}
              </div>

              {/* Days */}
              <div className="sdp-days-grid mt-1">
                {Array(firstDay).fill(null).map((_, i) => (
                  <div key={`e${i}`} className="sdp-cell" />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => (
                  <div key={day} className={cellCls(day)}>
                    <div
                      className={dayCls(day)}
                      onClick={() => handleDayClick(day)}
                      onMouseEnter={() => isRange && picking && setHover(dayStr(day))}
                      onMouseLeave={() => setHover(null)}
                    >
                      {day}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>

          {/* Footer — selected label + Cancel / Apply */}
          <div className="sdp-footer">
            <span className="sdp-sel-label">
              {panelStart
                ? (panelStart === panelEnd || !panelEnd
                    ? panelStart
                    : `${panelStart}  →  ${panelEnd}`)
                : ""}
            </span>
            <div className="sdp-footer-btns">
              <button className="sdp-btn-cancel" onClick={() => setOpen(false)}>Cancel</button>
              <button className="sdp-btn-apply"  onClick={handleApply}>Apply</button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default SalesDatePicker;
